import { isIOS } from 'mobile-device-detect';
import Game from '../../Game';
import { WORLDS } from '../../System/World';
import { Random } from '../../Utility/Random';
import {
    AdvertisementArea,
    AdvertisementType,
    IAdvertisementPayload,
    RewardType,
} from '../../Utility/SDK/Advertisement';
import SDKIntegrationsHelper from '../../Utility/SDK/SDKIntegrationsHelper';
import { AdGameArea } from '../../Utility/Tracking/DataSet/Ad';
import {
    TActionType,
    TransactionType,
} from '../../Utility/Tracking/DataSet/Transaction';
import { gameTracking } from '../../Utility/Tracking/FBLegacyTracking';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { track, TrackMethod } from '../../Utility/Tracking/Track';
import { trackTransaction } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import { AsyncActionStatus } from '../AsyncAction';
import { BoosterType } from '../Card/BoosterType';
import CardStateHelper from '../Card/CardStateHelper';
import { checkHelperFriendEnabled } from '../Level/HelperFriend/HelperFriendStateHelper';
import {
    initialLevelState,
    LevelStatus,
    LevelType,
} from '../Level/ILevelState';
import { getDefaultBoosterPrices } from '../Level/LevelStateHelper';
import { IPlayerState } from '../Player/IPlayerState';
import {
    setBoosterPlusFive,
    setCoins,
    setLives,
} from '../Player/PlayerInventoryHelper';
import { levelStart, setLevels } from '../Player/PlayerLevelHelper';
import { skipMission } from '../Player/PlayerMissionHelper';
import {
    addShortcut,
    consumePurchase,
    requestLife,
    sendGift,
    sendLife,
    subscribeBotOptIn,
} from '../Player/PlayerStateHelper';
import { setWheelSpins } from '../Player/PlayerWheelHelper';
import { ProductID } from '../Player/Product';
import { initialUIState } from '../UI/IUIState';
import {
    hidePopup,
    nextPopup,
    showPopup,
    showPrioPopup,
} from '../UI/UIStateHelper';
import {
    BotOptStatus,
    CrossPromoStatus,
    ShortcutStatus,
} from '../User/UserActions';
import {
    checkBotOptIn,
    checkCreateShortcut,
    checkCrossPromo,
} from '../User/UserReducer';
import { IContextResponse, ResponseStatus, ResponseType } from './GameActions';
import { ContextType } from './IContext';
import { GameScene, IGameState } from './IGameState';

export default class GameStateHelper {
    public static loadWorld(
        state: IGameState,
        world: number,
        clearUI: boolean = true
    ): IGameState {
        let UIState = clearUI ? initialUIState : state.UIState;

        // Validate world number.
        world = Math.min(WORLDS.length, Math.max(1, world));

        // Check if cross promo should be enabled.
        state = checkCrossPromo(state);

        // Check if bot-opt-in should be enabled.
        state = checkBotOptIn(state);

        state = checkCreateShortcut(state);

        if (state.UIState.queue.length > 0) {
            UIState = nextPopup(state.UIState);
        }

        return {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.WORLD,
            },
            worldState: { worldNumber: world },
            playerState: { ...state.playerState, activeWorld: world },
            levelState: initialLevelState, // Reset level state.
            UIState: UIState,
        };
    }

    public static loadLevel(
        state: IGameState,
        levelNumber: number,
        levelPath: string,
        levelType: LevelType,
        attempts: number = 1
    ): IGameState {
        levelNumber = Math.max(1, levelNumber);

        state = levelStart(state, levelNumber, levelType);

        state = {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.LEVEL,
            },
            levelState: {
                ...state.levelState,
                levelNumber: levelNumber,
                levelPath: levelPath,
                levelType: levelType,
                status: LevelStatus.LOADING,
                attempts: attempts,
                score: 0,
                // Reset all booster prices.
                boosterPrices: getDefaultBoosterPrices(),
            },
            UIState: initialUIState, // Reset UI state.
            userState: {
                ...state.userState,
            },
        };

        state = checkHelperFriendEnabled(state);

        track('mLevel', levelNumber);
        track('missionID', `level_saga_${levelNumber}`);
        track('missionName', `Level ${levelNumber}`);
        track('mLevelType', 'ALL_CARDS');
        track('mRetryNum', attempts - 1);

        // Subtract a life when the level starts.
        return state;
    }

    public static restoreContext(
        state: IGameState,
        response: IContextResponse
    ): IGameState {
        state = {
            ...state,
            gamePaused: false,
            context: {
                current: ContextType.DEFAULT,
                audioEnabled: true,
                response: response,
            },
        };

        // Handle context switch failures.
        if (response.status === ResponseStatus.FAIL) {
            switch (response.responseType) {
                case ResponseType.DISPLAY_ADVERTISEMENT:
                    // Show error message pop-up for rewarded ads only.
                    if (
                        response.data.placement.type ===
                        AdvertisementType.REWARDED
                    ) {
                        return {
                            ...state,
                            UIStatePrio: showPrioPopup(
                                state.UIStatePrio,
                                'errorMenu',
                                {
                                    title: '',
                                    body: Game.locale.getText('NoAD'),
                                }
                            ),
                        };
                    } else {
                        // No errors if interstitial ad fails to load.
                        return state;
                    }
                case ResponseType.PURCHASE_PRODUCT:
                    return {
                        ...state,
                        UIStatePrio: showPrioPopup(
                            state.UIStatePrio,
                            'errorMenu',
                            {
                                title: '',
                                body: Game.locale.getText('PurchaseFailed'),
                            }
                        ),
                    };
                case ResponseType.SUBSCRIBE_BOT_OPT_IN:
                    return {
                        ...state,
                        userState: {
                            ...state.userState,
                            subscribeBot: BotOptStatus.DISMISSED,
                        },
                        UIState: hidePopup(state.UIState, 'botOptInMenu', null),
                    };
                case ResponseType.SWITCH_GAME:
                    return state;
                case ResponseType.CREATE_SHORTCUT:
                    return {
                        ...state,
                        UIState: hidePopup(
                            state.UIState,
                            'createShortcutMenu',
                            null
                        ),
                    };
            }

            return state;
        }

        // Handle context switch success.
        switch (response.responseType) {
            case ResponseType.PURCHASE_PRODUCT:
                state = consumePurchase(state, response.data);
                return {
                    ...state,
                    // Close the shop pop-up.
                    UIState: hidePopup(state.UIState, 'shopMenu', null),
                };
            case ResponseType.SHARE_BEAT_HIGHSCORE_LEVEL:
                return {
                    ...state,
                    UIState: hidePopup(state.UIState, 'scoreBeatenMenu', null),
                };
            case ResponseType.SHARE_POSTCARD:
                return {
                    ...state,
                    UIState: hidePopup(state.UIState, 'postcardMenu', null),
                };
            case ResponseType.SHARE_SCREENSHOT:
                gameTracking(FBTrackingEvent.Prizewheel_Share);
                return state;
            case ResponseType.SEND_LIFE:
                gameTracking(FBTrackingEvent.Life_send);
                state = {
                    ...state,
                    playerState: sendLife(state.playerState, response.data),
                };
                trackTransaction(
                    state,
                    TransactionType.REWARD,
                    TActionType.SOCIAL
                );
                return state;
            case ResponseType.REQUEST_LIFE:
                gameTracking(FBTrackingEvent.Life_request);
                state = {
                    ...state,
                    playerState: requestLife(state.playerState, response.data),
                };
                trackTransaction(
                    state,
                    TransactionType.REWARD,
                    TActionType.SOCIAL
                );
                return state;
            case ResponseType.SEND_GIFT:
                gameTracking(FBTrackingEvent.Design_SH_Gift_Claim);
                state = {
                    ...state,
                    playerState: sendGift(state.playerState, response.data),
                };
                trackTransaction(
                    state,
                    TransactionType.REWARD,
                    TActionType.SOCIAL
                );
                return state;
            case ResponseType.SHARE_3_STAR_REWARD:
                gameTracking(FBTrackingEvent.three_star_share_success);

                return {
                    ...state,
                    UIState: hidePopup(
                        state.UIState,
                        'threeStarRewardMenu',
                        null
                    ),
                };
            case ResponseType.DISPLAY_ADVERTISEMENT:
                return GameStateHelper.adComplete(state, response.data.payload);
            case ResponseType.SUBSCRIBE_BOT_OPT_IN:
                state = {
                    ...state,
                    playerState: subscribeBotOptIn(state.playerState),
                    userState: {
                        ...state.userState,
                        subscribeBot: BotOptStatus.SUBSCRIBED,
                    },
                    UIState: hidePopup(state.UIState, 'botOptInMenu', null),
                };

                trackTransaction(
                    state,
                    TransactionType.REWARD,
                    TActionType.REWARD
                );
                return state;
            case ResponseType.SWITCH_GAME:
                // This code is likely not executed since Facebook does not resolve switchGameAsync before exiting.
                return {
                    ...state,
                    userState: {
                        ...state.userState,
                        crossPromo: CrossPromoStatus.SWITCHED,
                    },
                };
            case ResponseType.CREATE_SHORTCUT:
                state = {
                    ...state,
                    playerState: addShortcut(state.playerState),
                    userState: {
                        ...state.userState,
                        createShortcut: ShortcutStatus.ADDED,
                    },
                    UIState: hidePopup(
                        state.UIState,
                        'createShortcutMenu',
                        null
                    ),
                };
                trackTransaction(
                    state,
                    TransactionType.REWARD,
                    TActionType.REWARD
                );
                return state;
        }

        return state;
    }

    public static setContext(
        state: IGameState,
        context: ContextType,
        data: any
    ): IGameState {
        let audioEnabled: boolean = true;

        switch (context) {
            case ContextType.DEFAULT:
                break;
            case ContextType.ADD_GATE_FRIEND:
                SDKIntegrationsHelper.sendGateUnlockFriendRequest();
                break;
            case ContextType.PURCHASE_PRODUCT:
                SDKIntegrationsHelper.handlePurchaseProduct(data);
                break;
            case ContextType.INVITE_FRIEND:
                gameTracking(FBTrackingEvent.Design_GH_Invite);
                SDKIntegrationsHelper.inviteFriend();
                break;
            case ContextType.CHALLENGE_FRIEND:
                gameTracking(FBTrackingEvent.Win_Invite_Send);
                SDKIntegrationsHelper.challengeFriend();
                break;
            case ContextType.SEND_GIFT:
                gameTracking(FBTrackingEvent.Design_SH_Gift_Show);
                if (isIOS) {
                    // Updated policy states that sending and receiving resources between players is no longer allowed.
                    return GameStateHelper.restoreContext(state, {
                        status: ResponseStatus.SUCCESS,
                        responseType: ResponseType.SEND_GIFT,
                        data: data.friendId,
                    });
                } else {
                    SDKIntegrationsHelper.sendGift(data.friendId);
                }
                break;
            case ContextType.SEND_LIFE:
                SDKIntegrationsHelper.sendLife(data.friendId);
                break;
            case ContextType.REQUEST_LIFE:
                SDKIntegrationsHelper.requestLife(data.friendId);
                break;
            case ContextType.SHARE_BEAT_HIGHSCORE_LEVEL:
                SDKIntegrationsHelper.shareBeatHighscoreLevel(
                    data.levelNumber,
                    data.friendId
                );
                break;
            case ContextType.SHARE_3_STAR_REWARD:
                SDKIntegrationsHelper.share3StarReward(data.levelNumber);
                break;
            case ContextType.SHARE_POSTCARD:
                SDKIntegrationsHelper.sharePostcard(
                    data.gameObjectId,
                    data.worldName
                );
                break;
            case ContextType.SHARE_SCREENSHOT:
                SDKIntegrationsHelper.shareScreenshot(
                    data.message,
                    data.payloadData
                );
                break;
            case ContextType.DISPLAY_ADVERTISEMENT:
                track('adID', Random.ID());
                switch (state.activeScene.result) {
                    case GameScene.LEVEL:
                        track('adGameArea', AdGameArea.LEVEL);
                        if (state.levelState.levelNumber) {
                            track('adLevelNum', state.levelState.levelNumber);
                        }
                        if (state.levelState.levelType) {
                            track('adLevelType', state.levelState.levelType);
                        }
                        break;
                    case GameScene.WORLD:
                        track('adGameArea', AdGameArea.MAP);
                        break;
                }
                audioEnabled = false;
                SDKIntegrationsHelper.displayAdvertisement(
                    data.placement,
                    state,
                    data.payload
                );
                break;
            case ContextType.SUBSCRIBE_BOT_OPT_IN:
                SDKIntegrationsHelper.subscribeBotOptIn();
                break;
            case ContextType.CREATE_SHORTCUT:
                SDKIntegrationsHelper.createShortcut();
                break;
            case ContextType.SWITCH_GAME:
                SDKIntegrationsHelper.switchGame(data.gameId);
                break;
        }

        return {
            ...state,
            gamePaused: true,
            context: {
                current: context,
                audioEnabled: audioEnabled,
                request: {
                    context: context,
                    data: data,
                },
            },
        };
    }

    public static checkDisplayInterstitial(state: IGameState): IGameState {
        // Interstitial ads triggered every 2 levels starting at level 10 and
        // only if it's not a paying user.
        if (
            state.playerState.purchases === 0 &&
            state.levelState.levelNumber >= 10 &&
            state.levelState.levelNumber % 2 === 0
        ) {
            return this.setContext(state, ContextType.DISPLAY_ADVERTISEMENT, {
                placement: {
                    id: 0 /*changed from Game.config.adInterstitialID*/,
                    type: AdvertisementType.INTERSTITIAL,
                },
                payload: {
                    g_gameArea: AdvertisementArea.LEVEL_COMPLETE,
                    g_rewardType: RewardType.NONE,
                },
            });
        }

        return state;
    }

    public static adComplete(
        state: IGameState,
        payload: IAdvertisementPayload
    ): IGameState {
        let playerState: IPlayerState = state.playerState;
        switch (payload.g_rewardType) {
            case RewardType.NONE:
                break;
            case RewardType.THREE_STAR_REWARD:
                gameTracking(FBTrackingEvent.VideoAdWatch_3star_present);
                break;
            case RewardType.SHOP_COINS_100:
                playerState = setCoins(
                    playerState,
                    playerState.coins + 100,
                    true
                );
                track('tAdCoins', 100, TrackMethod.ADD);
                trackTransaction(state, TransactionType.AD, TActionType.AD);
                return {
                    ...state,
                    playerState: playerState,
                    UIState: hidePopup(state.UIState, 'shopMenu', null),
                };
            case RewardType.COINS_100:
                playerState = setCoins(
                    playerState,
                    playerState.coins + 100,
                    true
                );
                track('tAdCoins', 100, TrackMethod.ADD);
                trackTransaction(state, TransactionType.AD, TActionType.AD);
                return {
                    ...state,
                    UIState: hidePopup(state.UIState, 'noCoinsMenu', null),
                };
            case RewardType.LIVES_1:
                gameTracking(FBTrackingEvent.VideoAdWatchGetLife);
                playerState = setLives(
                    playerState,
                    playerState.lives + 1,
                    true
                );
                trackTransaction(state, TransactionType.AD, TActionType.AD);
                return {
                    ...state,
                    playerState: playerState,
                    UIState: hidePopup(state.UIState, 'noLivesMenu', null),
                };
            case RewardType.PLUS_FIVE_CARDS:
                gameTracking(FBTrackingEvent.VideoAdWatchFiveCards);
                playerState = setBoosterPlusFive(playerState, 1);
                trackTransaction(state, TransactionType.AD, TActionType.AD);
                // Add a free booster.
                state = {
                    ...state,
                    playerState: playerState,
                };
                trackTransaction(state, TransactionType.AD, TActionType.AD);
                // Immediately play the free booster.
                return CardStateHelper.playBooster(
                    state,
                    BoosterType.PLUS_FIVE
                );
            case RewardType.MISSION_SKIP:
                gameTracking(FBTrackingEvent.VideoAdWatchSkipMission);
                state = {
                    ...state,
                    playerState: skipMission(playerState, payload.missionIndex),
                };

                return {
                    ...state,
                    UIState: showPopup(state.UIState, 'receiveMissionMenu', {}),
                };
            case RewardType.PRIZE_WHEEL_SPIN:
                gameTracking(FBTrackingEvent.VideoAdWatchWheel);
                state = {
                    ...state,
                    playerState: setWheelSpins(
                        state.playerState,
                        state.playerState.wheelSpins + 1
                    ),
                };
        }

        return state;
    }

    public static handleRestoredProgressData(
        state: IGameState,
        restoredPlayerState: IPlayerState
    ): IGameState {
        state = {
            ...setLevels(
                state,
                LevelType.SAGA,
                restoredPlayerState.levels.saga
            ),
        };

        state.playerState.dataRestoreCounter =
            restoredPlayerState.dataRestoreCounter;

        return state;
    }

    public static handleUnconsumedPurchases(
        state: IGameState,
        unconsumedPurchases: ProductID[]
    ): IGameState {
        unconsumedPurchases.forEach(productID => {
            state = consumePurchase(state, productID);
        });
        return state;
    }
}
